/* import __COLOCATED_TEMPLATE__ from './disconnect-button.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { action } from '@ember/object';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import type IntlService from 'embercom/services/intl';

interface Args {}

export default class DisconnectButton extends Component<Args> {
  @service declare appService: AppService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare intl: IntlService;
  @service declare notificationsService: {
    notifyConfirmation: (msg: string) => void;
    notifyResponseError: (e: Error, messages: { default: string }) => void;
  };

  @action async disconnectApp() {
    let isConfirmed = await this.intercomConfirmService.confirm({
      body: this.intl.t(
        'standalone.salesforce.setup.connect.disconnect-button.confirmation-message',
        {
          environmentLabel: this.salesforceConfig?.environmentLabel,
        },
      ),
      confirmButtonText: this.intl.t(
        'standalone.salesforce.setup.connect.disconnect-button.confirm-button-label',
      ),
      primaryButtonType: 'primary-destructive',
    });

    try {
      if (isConfirmed) {
        await this.salesforceConfig?.disconnectApp();
        this.notificationsService.notifyConfirmation(
          this.intl.t('standalone.salesforce.setup.connect.disconnect-button.success-message'),
        );
      }
    } catch (e) {
      this.notificationsService.notifyResponseError(e, {
        default: this.intl.t('standalone.salesforce.setup.connect.disconnect-button.error-message'),
      });
    }
  }

  get isFinLive() {
    return this.finStandaloneService.salesforcePlatform.finIsLive;
  }

  get salesforceConfig(): SalesforceConfiguration | void {
    if (!this.appService.app.isStandaloneApp) {
      return;
    }
    return this.finStandaloneService.salesforcePlatform?.salesforceConfig;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::Setup::Connect::DisconnectButton': typeof DisconnectButton;
    'standalone/salesforce/setup/connect/disconnect-button': typeof DisconnectButton;
  }
}
